import React, { Dispatch, SetStateAction } from "react";
import { Layout } from "react-grid-layout";
import { MoneyApi } from "go-core/api/types";
import { SegmentType } from "go-segment/components/types";
import { DashboardApi } from "../../../services/Api/Organization/types";

export enum DashboardType {
	ORGANIZATION = "ORGANIZATION",
	GROUP_ORGANIZATIONS = "GROUP_ORGANIZATIONS",
}

export enum DashboardAggregateType {
	TOTAL_MONEY = "total_money",
	TRNSACTION_COUNT = "transaction_count",
	AVERAGE_MONEY = "average_money",
}

export enum DashboardReportType {
	CATEGORY = "Category",
	PAYMENT = "Payment",
	PRODUCT = "Product",
	SALES = "Sales",
}

export enum DashboardClientInfomationsType {
	AVERAGE_MONEY = "average_money",
	AVERAGE_PER_GUEST_MONEY = "average_per_guest_money",
	NUMBER_OF_GUESTS = "number_of_guests",
}

export interface DashboardClientInfomationsConfig {
	title: string;
	icon: JSX.Element;
	value: MoneyApi | number;
	compareValue: MoneyApi | number;
	seeMoreLink: string;
}

export enum DashboardGridItem {
	ALERTS = "ALERTS",
	CLIENT_DETAILS = "CLIENT_DETAILS",
	LIVE = "LIVE",
	CALL_RECORDER = "CALL_RECORDER",
	SALES_ADVANCED = "SALES_ADVANCED",
	PAYMENT = "PAYMENT",
	PRODUCT = "PRODUCT",
	CATEGORY = "CATEGORY",
	SALES = "SALES",
}

export enum ActionCausedGridItemResize {
	EMPTY_DATA,
	VERTICAL_CHART,
	HORIZONTAL_CHART,
	BOX,
}

export const reportTypes = [
	"PRODUCT_REPORT",
	"CATEGORY_REPORT",
	"PAYMENT_REPORT",
	"SALES_REPORT",
	"SALES_ADVANCED_REPORT",
] as const;
export type ReportType = (typeof reportTypes)[number];

export type DashboardCardConfig = {
	title: string | JSX.Element;
	reportNameToRedirectTo: string;
	type: ReportType;
	chartDataRangeType: ChartDataRangeType;
	chartType: ChartType;
	chartConfigName: keyof DashboardChartConfig;
	cursorType: React.CSSProperties["cursor"];
};

export const salesAggregateOptions = [
	"total_money",
	"transaction_count",
	"average_money",
	"extended.average_per_guest_money",
	"net_profit_money",
] as const;

export const orderItem = [
	"discount_count",
	"discount_money",
	"net_production_money",
	"net_profit_money",
	"net_total_money",
	"product_quantity",
	"sub_total_money",
	"tax_money",
	"total_money",
	"transaction_count",
	"average_money",
] as const;

export type AggregateOption = (typeof salesAggregateOptions)[number] | (typeof orderItem)[number];

export type ChartConfig = {
	type: ChartType;
	range: ChartDataRangeType;
	aggregate: AggregateOption;
};

export type DashboardChartConfig = {
	reportCategory: ChartConfig;
	reportPayment: ChartConfig;
	reportProduct: ChartConfig;
	reportSales: ChartConfig;
	reportSalesAdvanced: ChartConfig;
};

export enum ChartType {
	LINE = "LINE",
	VERTICAL_BAR = "VERTICAL_BAR",
	HORIZONTAL_BAR = "HORIZONTAL_BAR",
	PROGRESS_BAR = "PROGRESS_BAR",
	BOX = "BOX",
}

export const chartDataRangeOptions = ["HOUR_OF_DAY", "DAY_OF_WEEK", "DAY_OF_MONTH", "DATE"] as const;
export type ChartDataRangeType = (typeof chartDataRangeOptions)[number];

export interface DashboardEditConfig<T extends keyof Layout = never> {
	mobileGridConfig: {
		general: Layout[];
		extended: Layout[];
	};
	nonMobileGridConfig: Omit<Layout, T>[];
}

export interface DraggableComponentProps {
	style?: React.CSSProperties;
	className?: string;
	onMouseDown?: React.MouseEventHandler;
	onMouseUp?: React.MouseEventHandler;
	onTouchEnd?: React.TouchEventHandler;
	key: DashboardGridItem;
	setNonMobileLayout: Dispatch<SetStateAction<ReactGridLayout.Layout[]>>;
}

export interface DashboardSegmentValue {
	dashboardEdit: DashboardEditConfig<"w" | "h">;
	chartsConfig: DashboardChartConfig;
	filtersConfig: DashboardFilterConfigType;
}

export interface DashboardAllSegments extends SegmentType {
	value: DashboardSegmentValue;
}

export interface DashboardSegmentsConfig {
	allSegments: DashboardAllSegments[];
	selectedSegment: DashboardAllSegments;
}

export type DashboardInitialStateType = {
	data: DashboardApi | undefined;
	filtersConfig: DashboardFilterConfigType;
	chartsConfig: DashboardChartConfig;
	dashboardEdit: DashboardEditConfig;
	segmentsConfig: DashboardSegmentsConfig;
};

export type DashboardFilterConfigType = {
	selectedFilter: {
		dateRange: string;
		organizationIds?: string;
	};
};

export type DashboardReportFilter = {
	type?: string;
	defaultGroup: string;
	chartType: string;
	organizationId?: number;
	date?: string;
	dateRange: string;
	organizationIds?: string;
	size?: number;
	include?: string;
	dashboardType: DashboardType;
};

export interface DashboardOrganizationOption {
	id: number;
	label: string;
}

export enum DashboardSegmentSaveFunctionMode {
	SAVE_NEW_SEGMENT,
	SAVE_EXISTING_SEGMENT,
}

export interface FreshDashboardState {
	selectedDateRange: string;
	selectedOrganizationIds?: string;
	chartsConfig: DashboardChartConfig;
}

export interface SeeMoreLinkConfig {
	dateRange?: string;
	reportName?: string;
	withCompareParam?: boolean;
	organizationId?: number;
	customFilters?: string;
	customReportPath?: string;
	customGroups?: string;
}

export interface CallRecorderData {
	answeredCalls: number;
	missedCalls: number;
	previousPeriodAnsweredCalls: number;
	previousPeriodMissedCalls: number;
}
